import { ArrowRight, Mail, MessageSquare, Phone, type LucideIcon } from 'lucide-react';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';

type Channel = {
  kind: 'wa' | 'phone' | 'email';
  Icon: LucideIcon;
  badge: string;
  title: string;
  value: string;
  href: string;
  external?: boolean;
  desc: string;
  note: string;
};

const CHANNELS: ReadonlyArray<Channel> = [
  {
    kind: 'wa',
    Icon: MessageSquare,
    badge: 'Recommandé',
    title: 'WhatsApp',
    value: '+237 656 48 88 79',
    href: 'https://wa.me/237656488879',
    external: true,
    desc: "Le plus rapide. Envoie photos, vidéos de caisse, captures d'erreur. On te guide pas-à-pas.",
    note: '≤ 30 min · Lun-Sam',
  },
  {
    kind: 'phone',
    Icon: Phone,
    badge: 'Urgence',
    title: 'Téléphone',
    value: '+237 656 48 88 79',
    href: 'tel:+237656488879',
    desc: 'Ta caisse est bloquée pendant un rush ? Appelle directement. Un humain décroche aux heures ouvrées.',
    note: 'Lun-Ven · 8h-18h GMT+1',
  },
  {
    kind: 'email',
    Icon: Mail,
    badge: 'Détaillé',
    title: 'Email',
    value: 'contact@velko-pos.com',
    href: 'mailto:contact@velko-pos.com',
    desc: 'Pour les dossiers complexes, devis multi-boutiques, demandes presse ou partenariats. Pièces jointes acceptées.',
    note: 'Réponse ≤ 24h ouvrables',
  },
];

const TILE_BG: Record<Channel['kind'], string> = {
  wa: 'bg-lime-500 text-charcoal-800',
  phone: 'bg-blue-500 text-white',
  email: 'bg-terracotta-500 text-white',
};

export default function ContactChannelsV2() {
  return (
    <section className="bg-paper py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <SectionLabel>3 canaux · une équipe</SectionLabel>
        <h2 className="mt-3 font-marketing-display text-[clamp(32px,4.4vw,56px)] font-bold leading-[1] tracking-[-0.035em] text-charcoal-800">
          Choisis <span className="underline-lime">ton canal.</span>
        </h2>
        <p className="mt-4 max-w-xl font-marketing-body text-[17px] text-charcoal-600">
          La même équipe répond partout. À toi de choisir celui qui correspond à ton urgence et à
          ton contexte.
        </p>

        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {CHANNELS.map(({ kind, Icon, badge, title, value, href, external, desc, note }) => (
            <a
              key={kind}
              href={href}
              target={external ? '_blank' : undefined}
              rel={external ? 'noopener noreferrer' : undefined}
              className="group flex flex-col gap-5 border-2 border-charcoal-800 bg-white p-8 shadow-neobrutal-6 transition-transform duration-150 hover:-translate-x-[3px] hover:-translate-y-[3px] hover:shadow-neobrutal-10"
            >
              <div className="flex items-start justify-between">
                <span
                  className={`flex h-[52px] w-[52px] items-center justify-center border-2 border-charcoal-800 shadow-neobrutal-3 ${TILE_BG[kind]}`}
                >
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <span className="border-2 border-charcoal-800 bg-charcoal-800 px-2.5 py-1 font-marketing-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-lime-400">
                  {badge}
                </span>
              </div>

              <div>
                <h3 className="font-marketing-display text-[22px] font-bold tracking-[-0.02em] text-charcoal-800">
                  {title}
                </h3>
                <span className="mt-1 block font-marketing-mono text-[15px] font-medium text-charcoal-700">
                  {value}
                </span>
              </div>

              <p className="flex-1 font-marketing-body text-[14px] leading-[1.55] text-charcoal-600">
                {desc}
              </p>

              <div className="flex items-center justify-between border-t-2 border-dashed border-charcoal-200 pt-4">
                <span className="font-marketing-mono text-[11px] uppercase tracking-[0.06em] text-charcoal-500">
                  {note}
                </span>
                <span className="flex h-8 w-8 items-center justify-center bg-charcoal-800 text-lime-500 transition-transform duration-150 group-hover:translate-x-[2px]">
                  <ArrowRight className="h-[14px] w-[14px]" strokeWidth={2.5} />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
